<script setup lang="ts">
import { Tippy } from 'vue-tippy'
import type {
  NovaCalendarControlEmits,
  NovaCalendarControlProps,
} from './NovaCalendarControl.types'
import type { CalendarControlType } from '@components/NovaCalendarControlPanel/NovaCalendarControlPanel.types'
import { ServiceStartYear } from '@configs'

const emit = defineEmits<NovaCalendarControlEmits>()
const props = defineProps<NovaCalendarControlProps>()

const { dayjs } = useDayjs()
const { locale } = useI18n()
const year = ref<number>()
const years = ref<CalendarControlType[]>([])
const month = ref<number>()
const months = ref<CalendarControlType[]>([])
const monthsPanelRef = ref()
const yearsPanelRef = ref()
const showControlPanel = ref()
const allowMinDate = computed(() => dayjs(`${ServiceStartYear}-01-01`))
const allowMaxDate = computed(() => dayjs(`${dayjs().year()}-12-31`))
const callPrevMonthDisabled = computed(() => {
  const prevMonth = props.day.subtract(1, 'month')
  return prevMonth.diff(allowMinDate.value, 'milliseconds') < 0
})
const callNextMonthDisabled = computed(() => {
  const nextMonth = props.day.add(1, 'month')
  return nextMonth.diff(allowMaxDate.value, 'milliseconds') > 0
})

const createYearArray = (
  startYear: number,
  endYear: number
): CalendarControlType[] =>
  Array.from({ length: endYear - startYear + 1 }, (_, index) => {
    const _year = startYear + index

    return {
      index,
      value: _year,
      disabled: _year > dayjs().year(),
    }
  })

watch(
  () => [props.day, locale.value],
  ([curDay]) => {
    const _curDay = curDay as ReturnType<typeof dayjs>
    const instanceLocaleData = _curDay.localeData()
    year.value = _curDay.year()
    month.value = _curDay.month()
    months.value = instanceLocaleData
      .monthsShort()
      .map((item, index) => ({ index, value: item, disabled: false }))
    years.value = createYearArray(ServiceStartYear, year.value + 30)
  },
  { immediate: true }
)

const handleOnChangeControlPanel = (target: string, show: boolean) => {
  showControlPanel.value = show ? target : ''
}

const handleOnChangeMonth = (month: number) => {
  emit('update:day', dayjs(`${year.value}-${month + 1}-01`))
  monthsPanelRef.value?.hide()
}

const handleOnChangeYear = (index: number) => {
  emit(
    'update:day',
    dayjs(`${years.value[index].value}-${month.value! + 1}-01`)
  )
  yearsPanelRef.value?.hide()
}
</script>

<template>
  <div class="calendar-controls">
    <div class="inner">
      <NovaButtonIcon
        :icon="{ type: 'outline', name: 'chev-left' }"
        :disabled="callPrevMonthDisabled"
        class="change-month-button"
        @click="() => handleOnChangeMonth(month - 1)"
      />

      <div class="controls">
        <div class="control-wrap">
          <Tippy
            ref="monthsPanelRef"
            :append-to="'parent'"
            :interactive="true"
            :theme="'popover'"
            :placement="'bottom-start'"
            :trigger="'click'"
            :on-show="() => handleOnChangeControlPanel('month', true)"
            :on-hide="() => handleOnChangeControlPanel('month', false)"
          >
            <NovaCalendarControlButton
              :label="months[month].value"
              :on="showControlPanel === 'month'"
            />

            <template #content>
              <NovaCalendarControlPanel
                :controls="months"
                :active-index="month"
                @on-click-control="handleOnChangeMonth"
              />
            </template>
          </Tippy>
        </div>

        <div class="control-wrap">
          <Tippy
            ref="yearsPanelRef"
            :append-to="'parent'"
            :interactive="true"
            :theme="'popover'"
            :placement="'bottom-start'"
            :trigger="'click'"
            :on-show="() => handleOnChangeControlPanel('year', true)"
            :on-hide="() => handleOnChangeControlPanel('year', false)"
          >
            <NovaCalendarControlButton
              :label="year"
              :on="showControlPanel === 'year'"
            />

            <template #content>
              <NovaCalendarControlPanel
                :controls="years"
                :active-index="years.findIndex((item) => item.value === year)"
                @on-click-control="handleOnChangeYear"
              />
            </template>
          </Tippy>
        </div>
      </div>

      <NovaButtonIcon
        :icon="{ type: 'outline', name: 'chev-right' }"
        :disabled="callNextMonthDisabled"
        class="change-month-button"
        @click="() => handleOnChangeMonth(month + 1)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.calendar-controls {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 70px;
  padding: 0 16px;

  @include mobile {
    height: 38px;
  }

  .inner {
    display: inline-flex;
    align-items: center;
    gap: 36px;
  }

  .change-month-button {
    color: $color-bg-custom-4;
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 40px;
  }
}
</style>
